import React, { useEffect, useState } from "react";
import Loader from "./Loader";
import { Alert, Snackbar, Tooltip } from "@mui/material";
import { Helmet } from "react-helmet";
import Header from "./Header";
import Title from "./utils/Title/Title";
import {
  AutoAwesomeOutlined,
  CloseOutlined,
  DeleteOutline,
  DescriptionOutlined,
  FileUploadOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import "./AiAssessmentDashboard.css";
import CustomModal from "./utils/CustomModal/CustomModal";
import { listPDF, addPDF, deletePDF } from "../../services/pdfService";
import { generateAssessment } from "../../services/assessmentService";
import { getSignedUrl } from "../auth/util/util";

function UploadedFiles() {
  const [showLoader, setShowLoader] = useState(false);

  const [uploadedFilesList, setUploadedFilesList] = useState([]);

  const [openToast, setOpenToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [severityClass, setSeverityClass] = useState("success");
  const handleCloseToast = () => {
    setOpenToast(false);
  };

  // file
  const [selectedFile, setSelectedFile] = useState(null);
  const [openFileDeleteModal, setOpenFileDeleteModal] = useState(false);
  const handleCloseDeleteFileModal = () => {
    setOpenFileDeleteModal(false);
    setSelectedFile(null);
  };
  const handleOpenDeleteFileModal = (file) => {
    try {
      setShowLoader(true);
      console.log(file);

      if (!file) throw new Error("File not found");
      if (!file.cardItemID) throw new Error("File Id not found");
      if (!file.name) throw new Error("File name not found");

      setSelectedFile(file);
      setOpenFileDeleteModal(true);
    } catch (error) {
      console.error("Error (handleOpenDeleteFileModal) : ", error);
      setToastMsg("Unable to open File delete UI. Please try again later");
      setSeverityClass("error");
      setOpenToast(true);
    } finally {
      setShowLoader(false);
    }
  };

  // assessment
  const [openAssessmentGenerateModal, setOpenAssessmentGenerateModal] =
    useState(false);
  const [prompt, setPrompt] = useState("");
  const [numberOfQuestions, setNumberOfQuestions] = useState(10);
  const [totalMarks, setTotalMarks] = useState(100);
  const [passingMarks, setPassingMarks] = useState(40);
  const [assessmentType, setAssessmentType] = useState("MCQ");
  const [assessmentName, setAssessmentName] = useState("");
  const [assessmentDescription, setAssessmentDescription] = useState("");
  const [assessmentDuration, setAssessmentDuration] = useState(60);

  const handleOpenGenerateAssessmentModal = (file) => {
    try {
      setShowLoader(true);
      console.log(file);

      if (!file) throw new Error("File not found");
      if (!file.cardItemID) throw new Error("File Id not found");
      if (!file.name) throw new Error("File name not found");

      setSelectedFile(file);
      setOpenAssessmentGenerateModal(true);
    } catch (error) {
      console.error("Error (handleOpenGenerateAssessmentModal) : ", error);
      setToastMsg("Failed to open Generate Assessment UI. Please try again later");
      setSeverityClass("error");
      setOpenToast(true);
    } finally {
      setShowLoader(false);
    }
  };
  const handleCloseGenerateModal = () => {
    setOpenAssessmentGenerateModal(false);
    setSelectedFile(null);
    setPrompt("");
    setNumberOfQuestions(10);
    setTotalMarks(100);
    setPassingMarks(40);
    setAssessmentType("MCQ");
    setAssessmentName("");
    setAssessmentDescription("");
    setAssessmentDuration(60);
  };
  const handleGenerateAssessment = async () => {
    // console.log("Generating Assessment...");
    // console.log("Selected File: ", selectedFile);
    // console.log("Prompt: ", prompt);
    // console.log("Number of Questions: ", numberOfQuestions);
    // console.log("Total Marks: ", totalMarks);
    // console.log("Passing Marks: ", passingMarks);
    // console.log("Assessment Type: ", assessmentType);
    // console.log("Assessment Name: ", assessmentName);
    // console.log("Assessment Description: ", assessmentDescription);
    // console.log("Assessment Duration: ", assessmentDuration);

    try {
      setShowLoader(true);
      if (!selectedFile) throw new Error("File not found");
      if (!selectedFile.cardItemID) throw new Error("File Id not found");
      if (!selectedFile.name) throw new Error("File name not found");
      if (!prompt) throw new Error("Prompt not found");
      if (!numberOfQuestions) throw new Error("Number of Questions not found");
      if (!totalMarks) throw new Error("Total Marks not found");
      if (!passingMarks) throw new Error("Passing Marks not found");
      if (!assessmentType) throw new Error("Assessment Type not found");
      if (!assessmentName) throw new Error("Assessment Name not found");
      if (!assessmentDescription)
        throw new Error("Assessment Description not found");
      if (!assessmentDuration)
        throw new Error("Assessment Duration not found");
      if (assessmentType === "default")
        throw new Error("Assessment Type not selected");
      if (assessmentType === "MCQ") {
        setAssessmentType(0);
      }

      const data = {
        pdfId: selectedFile.cardItemID,
        prompt,
        count : numberOfQuestions,
        totalMarks,
        passingMarks,
        type : assessmentType,
        name : assessmentName,
        duration : assessmentDuration,
      };

      console.log("Data: ", data);
      const response = await generateAssessment(data);
      console.log("Response: ", response);
      if(!response?.status === 200) throw new Error("Assessment generation failed");
      setToastMsg("Assessment generated successfully. You can view it in the assessment dashboard");
      setSeverityClass("success");
      setOpenToast(true);
      handleCloseGenerateModal();
      
    } catch (error) {
      console.error("Error (handleGenerateAssessment) : ", error);
      setToastMsg("Failed to generate Assessment. Please try again later");
      setSeverityClass("error");
      setOpenToast(true);
      
    } finally {
      setShowLoader(false);
    }
  };

  // Fetching PDF list
  const fetchPDFList = async () => {
    try{
      setShowLoader(true);
      const { data } = await listPDF();
      console.log("Response: ", data);
      if(!data || data?.data?.pdfs?.length === 0){
        console.error("No data found");
        return;
      }
      
      const pdfs = [];
      for(let i = 0; i < data.data.pdfs.length; i++){
        let pdf = data.data.pdfs[i];
        pdfs.push({
          ...pdf,
          summary: pdf.summary.trim(),
          viewURL: await getSignedUrl(pdf.key, { level: 'public' })
        })
      }
      setUploadedFilesList(pdfs);
      console.log("Uploaded Files List: ", pdfs);
    } catch (error) {
      console.error("Error (fetchPDFList) : ", error);
      setToastMsg("Failed to fetch uploaded files. Please try again later");
      setSeverityClass("error");
      setOpenToast(true);
    } finally {
      setShowLoader(false);
    }
  }

  // File Upload
  const handleFileUpload = async (e) => {
    try {
      setShowLoader(true);
      if(!e.target.files || e.target.files.length === 0) return
      const file = e.target.files[0];
      console.log("File: ", file);
      if (!file) throw new Error("File not found");
      if (file.type !== "application/pdf") {
        throw new Error("File type not supported");
      }
      if (file.size > 20 * 1024 * 1024) {
        throw new Error("File size exceeds 5MB");
      }

      const formData = new FormData();
      formData.append("file", file);
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }

      const response = await addPDF(formData)
      console.log("Response: ", response);

      if(!response?.data?.data) throw new Error("File upload failed");

      const pdf = response.data.data;
      pdf.summary = pdf.summary.trim();
      pdf.viewURL = await getSignedUrl(pdf.key, { level: 'public' });
      setUploadedFilesList((prev) => [pdf, ...prev]);
      setToastMsg("File uploaded successfully");
      setSeverityClass("success");
      setOpenToast(true);

    } catch (error) {
      console.error("Error (handleFileUpload) : ", error);
      setToastMsg("Failed to upload file. Please try again later");
      setSeverityClass("error");
      setOpenToast(true);
    } finally {
      setShowLoader(false);
    }
  }

  // File Delete
  const deleteFile = async (id) => { 
    try {
      setShowLoader(true);
      console.log("Deleting file with id: ", id);
      if (!id) throw new Error("File Id not found");

      // Simulate API call
      const response = await deletePDF(id);
      console.log("Response: ", response);
      if(!response?.data?.data) throw new Error("File deletion failed");
      const pdfs = uploadedFilesList.filter((file) => file.cardItemID !== id);
      setUploadedFilesList(pdfs);
      setOpenFileDeleteModal(false);
      setSelectedFile(null);

      setToastMsg("File deleted successfully");
      setSeverityClass("success");
      setOpenToast(true);
    } catch (error) {
      console.error("Error (handleDelete) : ", error);
      setToastMsg("Something went wrong");
      setSeverityClass("error");
      setOpenToast(true);
    } finally {
      setShowLoader(false);
    }
  }

  useEffect(() => {
    const init = async () => {
      await fetchPDFList();
    }
    init();
  },[])

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Uploded Files</title>
      </Helmet>
      <Header />
      <Loader visible={showLoader} />

      <Snackbar
        open={openToast}
        autoHideDuration={3500}
        onClose={handleCloseToast}
      >
        <Alert
          variant="filled"
          onClose={handleCloseToast}
          severity={severityClass}
          sx={{ width: "100%" }}
        >
          {toastMsg}
        </Alert>
      </Snackbar>

      {/* Generate Assessment Modal */}
      <CustomModal
        open={openAssessmentGenerateModal}
        handleClose={handleCloseGenerateModal}
      >
        <h3
          style={{
            color: "#04a5a6",
          }}
        >
          Generate Assessment
        </h3>
        <div
          className="modal teal generate_assessment_modal"
          style={{ margin: "20px 0" }}
        >
          <div className="input_row">
            <div className="input_wrapper">
              <label>Selected File</label>
              <div className="selected_file">
                <DescriptionOutlined />
                <span>{selectedFile?.name || "No File Selected"}</span>
              </div>
            </div>
            <div className="input_wrapper">
              <label>Assessment Type</label>
              <select
                className="input_text select"
                value={assessmentType}
                onChange={(e) => setAssessmentType(e.target.value)}
              >
                <option value="default">Select Assessment Type</option>
                <option value={0}>MCQ</option>
                <option value={1}>Fill in the Blanks</option>
                <option value={2}>True or False</option>
                <option value={5}>Descriptive</option>
              </select>
            </div>
          </div>

          <div className="input_row">
            <div className="input_wrapper">
              <label>Assessment Name</label>
              <input
                type="text"
                className="input_text"
                placeholder="Maths - Know your Numbers - level 1"
                value={assessmentName}
                onChange={(e) => setAssessmentName(e.target.value)}
              />
            </div>
            <div className="input_wrapper">
              <label>Assessment Description</label>
              <input
                type="text"
                className="input_text"
                placeholder="Structured Assessment for level 1"
                value={assessmentDescription}
                onChange={(e) => setAssessmentDescription(e.target.value)}
              />
            </div>
          </div>

          <div className="input_row">
            <div className="input_wrapper">
              <label>Number Of Questions</label>
              <select
                className="input_text select"
                value={numberOfQuestions}
                onChange={(e) => setNumberOfQuestions(e.target.value)}
              >
                <option value="default">Select Number of Questions</option>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
              </select>
            </div>
            <div className="input_wrapper">
              <label>Duration</label>
              <select
                className="input_text select"
                value={assessmentDuration}
                onChange={(e) => setAssessmentDuration(e.target.value)}
              >
                <option value="default">Select Duration</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
                <option value={180}>3 hours</option>
              </select>
            </div>
          </div>

          <div className="input_row">
            <div className="input_wrapper">
              <label>Total Marks</label>
              <input
                type="number"
                className="input_text"
                placeholder="Total Marks"
                value={totalMarks}
                onChange={(e) => setTotalMarks(e.target.value)}
              />
            </div>
            <div className="input_wrapper">
              <label>Passing Marks</label>
              <input
                type="number"
                className="input_text"
                placeholder="Passing Marks"
                value={passingMarks}
                onChange={(e) => setPassingMarks(e.target.value)}
              />
            </div>
          </div>

          <div className="input_wrapper">
            <label>Prompt</label>
            <textarea
              className="input_text"
              placeholder="Enter your prompt here"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            ></textarea>
          </div>
        </div>

        <div
          style={{
            width: "100%",
            marginTop: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "20px",
          }}
        >
          <button
            style={{
              backgroundColor: "#04a5a6",
              color: "white",
              padding: "5px 7px",
              border: "none",
              borderRadius: "7px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
              fontSize: "14px",
            }}
            onClick={handleCloseGenerateModal}
          >
            <span>Cancel</span>
            <CloseOutlined style={{ fontSize: "16px" }} />
          </button>
          <button
            style={{
              backgroundColor: "#04a5a6",
              color: "white",
              padding: "5px 7px",
              border: "none",
              borderRadius: "7px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
              fontSize: "14px",
            }}
            onClick={handleGenerateAssessment}
          >
            <span>Generate</span>
            <AutoAwesomeOutlined style={{ fontSize: "16px" }} />
          </button>
        </div>
      </CustomModal>

      {/* Delete File Modal*/}
      <CustomModal
        open={openFileDeleteModal}
        handleClose={handleCloseDeleteFileModal}
      >
        <h3
          style={{
            color: "#04a5a6",
          }}
        >
          Delete File
        </h3>
        <div className="modal" style={{ margin: "20px 0" }}>
          <p>Selected File: </p>
          <div className="selected_file">
            <DescriptionOutlined />
            <span><Tooltip title={selectedFile?.name || ''}>{selectedFile?.name || "File not selected"}</Tooltip></span>
          </div>
        </div>
        <div
          style={{
            width: "100%",
            marginTop: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "20px",
          }}
        >
          <button
            style={{
              backgroundColor: "#04a5a6",
              color: "white",
              padding: "5px 7px",
              border: "none",
              borderRadius: "7px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
              fontSize: "14px",
            }}
            onClick={handleCloseDeleteFileModal}
          >
            <span>Cancel</span>
            <CloseOutlined style={{ fontSize: "16px" }} />
          </button>
          <button
            style={{
              backgroundColor: "#E33232",
              color: "white",
              padding: "5px 7px",
              border: "none",
              borderRadius: "7px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
              fontSize: "14px",
            }}
            onClick={() => {
              deleteFile(selectedFile?.cardItemID);
            }}
          >
            <span>Delete</span>
            <DeleteOutline style={{ fontSize: "16px" }} />
          </button>
        </div>
      </CustomModal>

      {/* Starts from here*/}
      <div className="custom_container">
        <Title>Uploaded Files</Title>

        {/* upload files */}
        <section className="upload_pdf_section" style={{ marginTop: "20px" }}>
          <div
            style={{
              backgroundColor: "#04a5a6",
              color: "white",
              padding: "7px 10px",
              border: "none",
              borderRadius: "7px",
              fontWeight: "600",
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
              boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.2)",
              position: "relative",
              minWidth: "fit-content",
              width: "fit-content",
              cursor: "pointer",
            }}
          >
            <input
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: "pointer",
              }}
              type="file"
              accept=".pdf"
              onChange={handleFileUpload}
            />
            <span>Upload PDF</span>
            <FileUploadOutlined
              style={{
                fontSize: "20px",
                marginLeft: "5px",
              }}
            />
          </div>
        </section>

        {/* view recently uploaded files */}
        <section
          style={{
            boxSizing: "border-box",
            position: "relative",
            padding: "20px 0",
            margin: "20px 0",
            zIndex: 1,
          }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              marginTop: "10px",
              gap: "30px",
              flexWrap: "wrap",
              boxSizing: "border-box",
            }}
          >
            {uploadedFilesList.map((file) => (
              <div className="card file" key={file.cardItemID}>
                <div className="summary">{file.summary}</div>
                <div className="bottom_bar">
                  <div className="name"><Tooltip title={file.name}>{file.name}</Tooltip></div>
                  <div className="actions">
                    <Tooltip title="View File">
                      <a href={file.viewURL} target="_blank" rel="noopener noreferrer">
                        <VisibilityOutlined className="action_btn" />
                      </a>
                    </Tooltip>
                    <Tooltip title="Generate Assessment">
                      <AutoAwesomeOutlined
                        className="action_btn"
                        onClick={() => {
                          handleOpenGenerateAssessmentModal(file);
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Delete File">
                      <DeleteOutline
                        className="action_btn red"
                        onClick={() => {
                          handleOpenDeleteFileModal(file);
                        }}
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

export default UploadedFiles;
